/**
 * ClosedBasketPayoff — "What happened?" panel for a CLOSED basket.
 *
 * A beginner who placed a structure and saw it square off has no idea what the position
 * even was. This reconstructs the payoff-at-expiry diagram from the STORED legs + entry
 * fills (so it works forever, no live data needed), anchored at the entry spot, and pairs
 * it with a plain-English description of the structure. Reuses payoffFromFills +
 * PayoffChartPro (the same machinery the running-basket view uses).
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import PayoffChartPro from '../charts/PayoffChartPro';
import { payoffFromFills } from '../../utils/payoffFromFills';

type Leg = { action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; quantity: number; symbol: string };
type Fill = { symbol?: string; fillPrice?: number | null; contractValue?: number };

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;

/** One-line, beginner-friendly description of what the structure was and its risk shape —
 *  derived from the numbers so it's always correct regardless of the strategy name. */
function describe(strategy: string, entryCredit: number | null, maxProfit: number | null, maxLoss: number | null): string {
  const credit = entryCredit != null && entryCredit >= 0;
  const definedLoss = maxLoss != null && Number.isFinite(maxLoss) && Math.abs(maxLoss) > 0;
  const cost = credit
    ? `You received premium up front (a credit)`
    : `You paid a premium up front (a debit)`;
  const risk = definedLoss
    ? `— it's a defined-risk trade: the most you could lose was ${fmtInr(Math.abs(Number(maxLoss)))}${maxProfit != null ? ` and the most you could make was ${fmtInr(Math.abs(Number(maxProfit)))}` : ''}.`
    : `— risk was open-ended, managed by the stop.`;
  return `${strategy}. ${cost} ${risk} The curve below is the profit/loss at expiry across underlying prices; the line marks the price when you entered.`;
}

const ClosedBasketPayoff: React.FC<{
  strategy: string;
  legs: Leg[];
  legFills?: Fill[];
  entrySpot?: number | null;
  entryCredit?: number | null;
  maxProfit?: number | null;
  maxLoss?: number | null;
  breakEvens?: number[] | null;
}> = ({ strategy, legs, legFills, entrySpot, entryCredit, maxProfit, maxLoss, breakEvens }) => {
  const base = useMemo(() => String(legs?.[0]?.symbol || '').split('-')[1] || '', [legs]);
  const isDelta = /^[CP]-/.test(String(legs?.[0]?.symbol || ''));
  const cv = Number(legFills?.[0]?.contractValue) || (isDelta ? (base === 'ETH' ? 0.01 : 0.001) : 1);

  const card = useMemo(() => {
    const fillBySym = new Map<string, number>();
    for (const f of legFills || []) if (f.symbol && f.fillPrice != null) fillBySym.set(f.symbol, Number(f.fillPrice));
    const fillLegs = (legs || []).map(l => ({
      action: l.action, optionType: l.optionType, strike: Number(l.strike),
      entryFill: fillBySym.has(l.symbol) ? fillBySym.get(l.symbol)! : null, quantity: Number(l.quantity),
    }));
    const c = payoffFromFills(fillLegs, cv, entrySpot ?? null, isDelta ? 85 : 1);
    if (c && (!c.breakEvens?.length) && breakEvens?.length) c.breakEvens = breakEvens;
    return c;
  }, [legs, legFills, cv, entrySpot, isDelta, breakEvens]);

  if (!card) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Payoff can't be reconstructed for this basket (entry fills weren't recorded).
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.4 }}>
        {describe(strategy, entryCredit ?? null, maxProfit ?? null, maxLoss ?? null)}
      </Typography>
      <PayoffChartPro basket={card} spotPrice={entrySpot && entrySpot > 0 ? Number(entrySpot) : 0} underlyingSymbol={base || undefined} />
      {(!entrySpot || entrySpot <= 0) && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          Entry price wasn't recorded for this (older) basket, so the entry marker is hidden — the payoff shape is still exact.
        </Typography>
      )}
    </Box>
  );
};

export default ClosedBasketPayoff;
